import type { TransmissionTaskType } from '../transmission/ITask'

/**
 * 消息类型
 */
export enum NoticeCategory {
  /**
   * 一般通知
   */
  NOTICE = 1,
  /**
   * 传输通知
   */
  TRANSMISSION = 2,
  /**
   * 异步动作通知
   */
  EVENT = 3,
}

/**
 * 通知动作
 */
export enum Action {
  /**
   * 查看传输列表
   */
  VIEW_TRANSMISSION = 0,
  /**
   * 跳转指定页面
   */
  NAV_TO = 1,
  /**
   * 引导： 新加设备
   */
  GUIDE_DEVICE_ADDING = 2,
  /**
   * 单纯的内容提示
   */
  CONTENT_PROMPT = 3,
  /**
   * 局域网证书提示
   */
  LAN_PROMPT = 4,
}

/**
 * 传输任务状态
 */
export enum TransmissionState {
  /**
   * 传输成功
   */
  DONE_SUCCEED = 0,
  /**
   * 传输失败
   */
  DONE_FAILED = 1,
  /**
   * 传输暂停
   */
  PAUSE = 2,
  /**
   * 传输中
   */
  ON_GOING = 3,
}

/**
 * 是否带有通知的类型
 */
export enum NoticeType {
  /**
   * 普通通知，没有按钮
   */
  INFO = 0,
  /**
   * 通用带按钮通知
   */
  COMMON_ACTION = 1,
}

/**
 * 消息基类，不能直接使用
 */
export interface BaseNotice {
  /**
   *  各自微应用或者基座代表的appId
   */
  appId: string
  /**
   * 可选项 是否是强类型的通知，默认是false
   *
   * true:是   false:不是
   */
  isEmphasized?: boolean
}

/**
 * 普通通知，比如系统通知
 */
export interface Notice extends BaseNotice {
  /**
   * 通知消息的具体内容
   */
  msg: string
  /**
   * 通知类型 是否带有按钮
   *
   * 0：不带 1：带按钮
   */
  type: NoticeType
}

/**
 * 带按钮通知(基类，不能直接使用)
 */
interface BaseActionNotice extends Notice {
  /**
   * 通知动作的类型
   *
   * 0：查看传输列表 1：跳转指定页面 2：引导： 新加设备
   */
  action: Action
  /**
   * optional, 如果不设置默认为框架根据action类型显示在界面
   */
  actionBtnLabel?: string
}

/**
 * 通用的带按钮的通知，点击按钮跳转到指定的中台路由
 */
export interface ActionNotice extends BaseActionNotice {
  /**
   * action === Action.NAV_TO  的时候，携带的路由路径，只需要使用业务内部的路由相对路径，
   *
   * 比如：'file/view'，
   */
  pagePath?: string
  /**
   * 如果对于pagePath需要传递额外的参数，则填写在该字段，当用户点击按钮时，基座会把这部分拼接在后面， 比如：这里传'url=xx.xx.x/xxx.docs&type=1', 基座跳转路由时是使用 '[业务的入口路由]/file/view?url=xx.xx.x/xxx.docs&type=1'
   */
  extraQuery?: string
}

/**
 * 传输消息的特有字段
 */

export interface TransmissionInfo {
  /**
   * 用于区分任务是由那个浏览器页/tab页创建的，目前传输列表以此为单位管理，不会把账号下的所有任务跨浏览器/tab也显示
   */
  webClientId: string
}

/**
 * 文件传输类（即用于文件上传或者下载）
 * 文件传输类的通知在通知中心显示，并且带有按钮，点击按钮固定跳转到传输列表的对应tab（上传tab or 下载tab）
 */
export interface TransmissionNotice extends BaseNotice {
  /**
   * 是否上传
   *
   * true 代表上传，false代表下载
   */
  isUpload: boolean
  /**
   * 用于显示类型进度信息，
   *
   * 比如： “正在上传xxx文件”，默认为“正在上传/下载 xxxx 文件”
   */
  subMsg?: string
  /**
   * target或者source device ID
   */
  targetDevice: string
  /**
   * optional，文件存储的目标路径，只有在文件上传的时候才需要设置
   */
  targetPath?: string
  /**
   * 文件名称
   */
  fileName: string
  /**
   * 文件总大小，单位：字节
   */
  fileSize: number
  /**
   * 当前已经传送的字节数
   */
  transmitted: number
  /**
   * 传输状态
   *
   * 0：传输成功 1：传输失败 2：暂停 3：传输进行中
   */
  state: TransmissionState
  /**
   * 上传完成后的资源地址
   */
  cloudPath?: string
  /**
   * 失败原因 目前主要区分是否是用户主动取消的
   */
  reason?: string
  /**
   * 压缩后的文件大小【端侧更新】,
   */
  targetZipSize?: number
  /**
   * 传输任务类型
   */
  businessType?: TransmissionTaskType
}

/**
 * 操作任务状态，
 */
export enum OperationState {
  /**
   * 操作成功
   */
  DONE_SUCCEED = 0,
  /**
   * 操作失败
   */
  DONE_FAILED = 1,
  /**
   * 操作中
   */
  ON_GOING = 2,
}

/**
 * 端侧异步操作类（比如：操作端侧文件移动、文件拷贝）
 */
export interface AsyncOperationEvent extends BaseNotice {
  /**
   * 业务端自行定义，web中台只是透传
   */
  operation: string
  /**
   * 可选字段，当涉及不同目录下操作的时候，这里表示操作的原始路径
   */
  source?: string
  /**
   * 作用为通知web接收方（比如文管）具体的发生变化的文件目录，方便文管判断是否刷新页面
   */
  target: string
  /**
   * 操作状态
   *
   * 0:成功 1：失败 2：进行中
   */
  state: OperationState
}
